import React from 'react';
import { useShortcut } from '@/hooks/KeyboardShortcuts';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandLoading,
} from '@/components/ui/command';
import { MachineGroup, useGetMachinesByNameQuery } from '@/generated/codegen';
import { useOptionalFilterState, useMakeFilteredUrl } from '@/hooks/useFilter';
import { isNotNullish } from '@/utils/nullish';
import { useHistory } from 'react-router-dom';
import { DialogTitle } from './ui/dialog';

import { SearchIcon } from 'lucide-react';
import { FaTractor } from '@react-icons/all-files/fa/FaTractor';
import { FaTruckPickup } from '@react-icons/all-files/fa/FaTruckPickup';
import { FaTruck } from '@react-icons/all-files/fa/FaTruck';
import { FaTrailer } from '@react-icons/all-files/fa/FaTrailer';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { cn } from '@/utils/utils';
import { getColor } from '@/utils/colors';
import { machineGroupToLabel } from '@/modules/machines/MachinesSchema';
import { Button } from './ui/button';
import { useNavGroups } from '@/hooks/useNavGroups';
import {
  companyCommandMenuRoutesDef,
  companyNavRoutesDef,
} from '@/modules/app/routes';
import { useThrottle } from '@/hooks/useThrottle';
import { MachineSearchNotice } from './Inputs/MachineSearchNotice';

type CommandMenuProps = { hide?: boolean };

export const CommandMenu: React.FC<CommandMenuProps> = ({ hide }) => {
  const filter = useOptionalFilterState();
  const make = useMakeFilteredUrl();
  const navGroups = useNavGroups([
    ...companyNavRoutesDef,
    ...companyCommandMenuRoutesDef,
  ]);
  const history = useHistory();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const throttledSearch = useThrottle(search, 200);
  const goTo = React.useCallback(
    (to: string, exact?: boolean) => {
      history.push(exact ? to : make(to));
      setOpen(false);
    },
    [history, make]
  );

  useShortcut(
    'meta+k,ctrl+k',
    'Menú rápido',
    () => setOpen(true),
    navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'cmd+k' : 'ctrl+k',
    { preventDefault: true }
  );
  const { data: machinesData, loading } = useGetMachinesByNameQuery({
    variables: filter.query
      ? {
          context: filter.query,
          filter: { query: throttledSearch },
          pagination: { offset: 0, limit: 50 },
        }
      : undefined,
    skip: !throttledSearch || !filter.query,
    fetchPolicy: 'cache-and-network',
  });
  const machines =
    machinesData?.getMachines && 'items' in machinesData.getMachines
      ? machinesData?.getMachines?.items
      : undefined;

  return (
    <>
      {!hide && (
        <Button
          variant="outline"
          aria-label="Buscar"
          className={cn(
            // Phones get an icon-only trigger: the text version does not fit beside the breadcrumbs
            'relative h-8 justify-start rounded-[0.5rem] bg-muted/50 text-sm font-normal text-muted-foreground shadow-none sm:w-full sm:pr-12 md:w-40 lg:w-56 xl:w-64'
          )}
          onClick={() => setOpen(true)}
        >
          <SearchIcon className="size-4 sm:hidden" />
          <span className="hidden sm:inline-flex">Buscar...</span>
          <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.3rem] hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-xs font-medium opacity-100 sm:flex text-muted-foreground">
            <span className="text-xs">
              {navigator.platform.toUpperCase().indexOf('MAC') >= 0
                ? '⌘'
                : 'ctrl'}
            </span>
            K
          </kbd>
        </Button>
      )}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <DialogTitle className="hidden">Comandos</DialogTitle>
        <CommandInput
          placeholder="Escribí un comando o buscá tus equipos..."
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          {loading ? (
            <CommandLoading>
              <LoadingSpinner />
            </CommandLoading>
          ) : (
            <CommandEmpty>No se encontraron resultados.</CommandEmpty>
          )}
          {!throttledSearch && (
            <CommandGroup heading="Equipos" forceMount>
              <div className="py-2 text-center text-xs">
                Escribe para buscar tus equipos.
              </div>
            </CommandGroup>
          )}
          {navGroups.map((group) => (
            <CommandGroup key={group.title} heading={group.title}>
              {group.items.map((item) =>
                item.items ? (
                  item.items.map((subitem) => (
                    <CommandItem
                      key={subitem.name}
                      onSelect={() => {
                        if (subitem.externalHref) {
                          window.open(
                            subitem.externalHref,
                            '_blank',
                            'noreferrer'
                          );
                          setOpen(false);
                        } else if (subitem.to) {
                          goTo(subitem.to, true);
                        }
                      }}
                    >
                      {item.label ?? item.name} {'>'}{' '}
                      {subitem.label ?? subitem.name}
                    </CommandItem>
                  ))
                ) : (
                  <CommandItem
                    key={item.name}
                    onSelect={() => (item.to ? goTo(item.to, true) : undefined)}
                  >
                    {item.label ?? item.name}
                  </CommandItem>
                )
              )}
            </CommandGroup>
          ))}
          {throttledSearch && (
            <CommandGroup heading="Equipos">
              {machines?.length ? (
                machines
                  .filter(isNotNullish)
                  .map((machine) => (
                    <MachineCommandItem
                      key={machine.id}
                      type={machine.type.group}
                      search={search}
                      value={machine.display_name ?? '@@'}
                      keywords={[
                        machine.type.type,
                        machineGroupToLabel[machine.type.group],
                        machine.brand,
                        machine.domain ?? '##',
                        machine.owner,
                        machine.name,
                        machine.model,
                      ]}
                      onSelect={() => goTo(`/machine/${machine.id}`)}
                    />
                  ))
              ) : (
                <div className="py-2 text-center text-xs">
                  No se encontraron equipos.
                </div>
              )}
            </CommandGroup>
          )}
          <MachineSearchNotice
            receivedCount={machines?.length ?? 0}
            totalItems={
              machinesData?.getMachines && 'items' in machinesData.getMachines
                ? machinesData.getMachines.page_info?.total_items
                : undefined
            }
            limit={50}
            loading={loading}
            active={!!throttledSearch && !!filter.query}
          />
        </CommandList>
      </CommandDialog>
    </>
  );
};

const MachineCommandItem: React.FC<{
  type: MachineGroup;
  search: string;
  value: string;
  keywords: string[];
  onSelect: () => void;
}> = ({ type, search, value, keywords, onSelect }) => {
  return (
    <CommandItem
      onSelect={onSelect}
      value={value}
      className={cn('gap-2')}
      keywords={keywords}
    >
      <MachineIcon type={type} />
      <span>
        <HighlightMachine search={search} value={value} keywords={keywords} />
      </span>
    </CommandItem>
  );
};

const MachineIcon: React.FC<{ type: MachineGroup }> = ({ type }) => {
  switch (type) {
    case MachineGroup.ActivoFijo:
      return <FaTrailer color={getColor('gray')} />;
    case MachineGroup.Camion:
      return <FaTruck color={getColor('gray')} />;
    case MachineGroup.EquipoPesado:
      return <FaTractor color={getColor('gray')} />;
    case MachineGroup.Movilidad:
      return <FaTruckPickup color={getColor('gray')} />;
  }
};

const HighlightMachine: React.FC<{
  search: string;
  value: string;
  keywords: string[];
}> = ({ value, search, keywords }) => {
  const searchWords = search.split(' ').map((s) => s.toLocaleLowerCase());
  const firstKeywordWithHit = keywords.find((k) =>
    searchWords.find((s) => k.toLocaleLowerCase().includes(s))
  );
  return (
    <span>
      <Higlight value={value} searchWords={searchWords} />
      {firstKeywordWithHit && !value.includes(firstKeywordWithHit) ? (
        <span className="text-muted-foreground">
          {' '}
          - (<Higlight value={firstKeywordWithHit} searchWords={searchWords} />)
        </span>
      ) : null}
    </span>
  );
};

const Higlight: React.FC<{ value: string; searchWords: string[] }> = ({
  value,
  searchWords,
}) => {
  const parts = value.split(new RegExp(`(${searchWords.join('|')})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) => (
        <span
          key={i}
          className={cn({
            'font-bold': searchWords.find(
              (s) => s === part.toLocaleLowerCase()
            ),
          })}
        >
          {part}
        </span>
      ))}
    </span>
  );
};
